<template>
  <div class="sub_contents">
    <div class="cont_center">
      <h1 class="title_page">매장 검색</h1>

      <!-- 검색 -->
      <div class="sub_line_box">
        <div class="input_area search">
          <h3 class="title_input">지역</h3>
          <div class="region-chip-list" aria-label="시도 선택">
            <button v-for="region in regionList" :key="region.value" type="button"
              :class="{ active: selectedSido === region.value }" @click="selectRegion(region)">
              {{ region.name }}
            </button>
          </div>
        </div>

        <div class="input_area search">
          <h3 class="title_input">매장명</h3>
          <InputText v-model="storeReqData.name" :placeholder="placeholderStore" @keyup.enter="search" />
        </div>

        <div class="btn_search_area">
          <button type="button" class="btn_type1" @click="search">검색</button>
        </div>
      </div>
      <!-- //검색 -->

      <div class="search_result_area">
        <!-- 로딩 레이어 -->
        <div v-if="storeData.isLoading.value || isManualLoading" class="loading_overlay">
          <div class="loading_content">
            <div class="loading_spinner"></div>
            <p class="loading_text">Loading...</p>
          </div>
        </div>

        <div class="flex items-center justify-between mb-4">
          <p class="search_result_num flex items-center justify-between mb-4 w-full">
            <span>검색 결과 총 <strong>{{ total }}</strong>개</span>
            <!-- <button class="btn_map_view" @click="openMapView(storeReqData.latitude, storeReqData.longitude)">
              지도로 보기
            </button> -->
          </p>
        </div>

        <!-- 리스트 -->
        <TableList>
          <template #col>
            <col style="width: 15.62%;" />
            <col style="width: 18.75%;" />
            <col style="width: 12.5%;" />
            <col />
            <col style="width: 12.34%;" />
            <col style="width: 7.96%;" />
          </template>
          <template #header>
            <th>브랜드명</th>
            <th>매장명</th>
            <th>전화번호</th>
            <th>주소</th>
            <th>혜택</th>
            <th>매장 위치</th>
          </template>
          <template #list>
            <!-- storeList 반복 렌더링 -->
            <tr v-for="(store, index) in storeList" :key="index">
              <td class="brand_name">{{ store.brandName }}</td>
              <td class="brand_shop_name">{{ store.storeName }}</td>
              <td>{{ store.phone }}</td>
              <td class="brand_address t_left">{{ store.address }}</td>
              <td>{{ store.benefit }}</td>
              <td :class="{ open: store.isOpen }">
                <button class="btn_shop_location"
                  @click.stop.prevent="openMap(index, store.latitude, store.longitude, $event)"
                  :class="{ open: store.isOpen }">매장위치 보기</button>

                <!-- 지도 영역 -->
                <div class="map_area" v-if="store.isOpen" @click.stop.prevent> <!-- .prevent 추가 -->
                  <div v-if="isPc" @click.stop.prevent> <!-- 이벤트 중단 추가 -->
                    <NaverMapPC :lat="store.latitude" :lng="store.longitude" :title="store.storeName"
                      :address="store.address" :is-ready="store.isReady" />
                  </div>
                  <div v-else @click.stop.prevent> <!-- 이벤트 중단 추가 -->
                    <NaverMapMo :lat="store.latitude" :lng="store.longitude" :title="store.storeName"
                      :address="store.address" :is-ready="store.isReady" />
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </TableList>
        <!-- //리스트 -->

        <PagingCom :currentPage="currentPage" :pageSize="pageSize" :totalItems="total" @pageChange="onPageChange" />
        <!-- //페이징 -->
      </div>
    </div>
  </div>

  <AlertPopup v-model="alertRequiredShow" title="알림">
    <template #layer_content>
      <p class="alert_txt">
        등록 된 좌표 정보가 없습니다.
      </p>
    </template>
  </AlertPopup>
</template>

<script>
import InputText from '@/components/InputText'
import TableList from '@/components/TableList'
import PagingCom from '@/components/PagingCom'
import { useQuery } from "@tanstack/vue-query";
import { getAxios } from "@/lib/AxiosCall";
import { ref, watch, onMounted, onBeforeUnmount, reactive, computed } from "vue";
import { getBranchUuid, getConfig } from '@/lib/Config';
import NaverMapPC from '@/components/NaverMap';
import NaverMapMo from '@/components/NaverMapMo';
import AlertPopup from '@/components/AlertPopup.vue';
import { useRoute } from "vue-router";

export default {
  components: {
    InputText,
    TableList,
    PagingCom,
    NaverMapPC,
    NaverMapMo,
    AlertPopup
  },
  data() {
    return {
      placeholderStore: '브랜드명 또는 매장명을 입력하세요.',
      isOpen: false,
      alertRequiredShow: false,
    }
  },
  setup() {
    const route = useRoute();

    const isPc = ref(false);
    const branchUuid = ref(getBranchUuid() || '');
    const brandUuid = ref(route.query.brandUuid || null);
    const regionList = ref([]);
    const storeList = ref([]);
    const selectedSido = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const isLocationReady = ref(false); // 위치 준비 상태
    const isManualLoading = ref(false); // 수동 로딩 상태 추가

    // 나이키 검색 시 전체 정렬된 데이터 캐싱
    const nikeFullSortedList = ref([]);

    // 나이키 검색 결과 고정 순서
    const nikeStoreOrder = [
      '나이키 홍대',
      '나이키 두타',
      '나이키 압구정',
      '나이키 아이파크몰용산',
      '나이키 롯데몰은평',
      '나이키 고잔',
      '나이키 AK플라자세종',
      '나이키 서산',
      '나이키 대구호림',
      '나이키 연제',
      '나이키 경성부경',
      '나이키 양산뉴타운'
    ];

    // storeReqData를 reactive로 변경하여 깊은 반응성 유지
    const storeReqData = reactive({
      page: 1,
      size: 10,
      longitude: 0,
      latitude: 0,
      sido: '',
      name: ''
    });

    // 매장 데이터 쿼리 - 안정적인 queryKey 사용
    const storeData = useQuery({
      queryKey: ['store-list'], // 고정된 키 사용
      queryFn: async () => {
        console.log('매장 API 호출 시작:', storeReqData);

        // 나이키 검색인 경우 전체 데이터 조회 (크기 제한 없음)
        let apiParams = { ...storeReqData };
        if (storeReqData.name && storeReqData.name.includes('나이키')) {
          console.log('나이키 검색 감지 - 전체 데이터 조회 요청');
          apiParams.page = 1;
          apiParams.size = 999; // 전체 데이터 조회
        }

        // 서버가 전체 조회(지역·검색어 없음)에 좌표를 함께 보내면 500을 반환하므로 좌표 없이 재조회
        let result;
        try {
          result = await getAxios("/hvm/v1/branch/store", apiParams);
        } catch (error) {
          if (!apiParams.latitude && !apiParams.longitude) throw error;
          console.warn('좌표 포함 조회 실패 - 좌표 없이 재조회:', error?.message);
          result = await getAxios("/hvm/v1/branch/store", { ...apiParams, latitude: 0, longitude: 0 });
        }
        console.log('매장 API 응답:', result);
        return result;
      },
      enabled: false, // 수동 호출
      retry: 1,
      retryDelay: 1000,
    });

    // 매장 데이터 변경 감시 - 강제 업데이트 추가
    watch(() => storeData.data.value, (data) => {
      console.log('매장 데이터 watch 실행:', data);

      if (data && data.page && data.page.list) {
        console.log('매장 리스트 업데이트:', data.page.list.length, '개');

        let sortedList = [...data.page.list];
        const pageSize = storeReqData.size || 10;
        const currentPage = storeReqData.page || 1;

        // 나이키 검색인 경우 고정된 순서 적용 + 페이징 처리
        if (storeReqData.name && storeReqData.name.includes('나이키')) {
          console.log('나이키 검색 감지 - 전체 데이터 정렬 후 페이징 적용');

          // 전체 데이터를 고정 순서대로 정렬
          sortedList.sort((a, b) => {
            const indexA = nikeStoreOrder.indexOf(a.storeName);
            const indexB = nikeStoreOrder.indexOf(b.storeName);

            // 고정 순서 목록에 있는 항목들을 우선 정렬
            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB; // 두 항목 모두 목록에 있으면 순서대로
            } else if (indexA !== -1) {
              return -1; // A만 목록에 있으면 앞으로
            } else if (indexB !== -1) {
              return 1; // B만 목록에 있으면 뒤로
            } else {
              // 둘 다 목록에 없으면 기본 정렬 (한글 자음순)
              const nameA = a.storeName || '';
              const nameB = b.storeName || '';
              return nameA.localeCompare(nameB, 'ko-KR', { sensitivity: 'base' });
            }
          });

          console.log('나이키 고정 순서 정렬 완료:', sortedList.map((s, idx) => `${idx + 1}. ${s.storeName}`));

          // 전체 정렬된 데이터 캐싱
          nikeFullSortedList.value = sortedList;

          // 현재 페이지에 해당하는 데이터만 추출
          const startIndex = (currentPage - 1) * pageSize;
          const endIndex = startIndex + pageSize;
          const paginatedList = sortedList.slice(startIndex, endIndex);

          console.log(`페이지 정보: 현재페이지=${currentPage}, 페이지크기=${pageSize}, 시작=${startIndex}, 끝=${endIndex}`);
          console.log('현재 페이지 데이터:', paginatedList.map((s, idx) => `${startIndex + idx + 1}. ${s.storeName}`));

          // 페이징된 데이터를 storeList에 할당
          storeList.value = [...paginatedList.map(store => ({
            ...store,
            isOpen: false,
            isReady: false
          }))];

          // 전체 총 개수 설정
          total.value = sortedList.length;
        } else {
          // 나이키가 아닌 경우 기본 정렬 (한글 자음순)
          sortedList.sort((a, b) => {
            const nameA = a.storeName || '';
            const nameB = b.storeName || '';
            return nameA.localeCompare(nameB, 'ko-KR', { sensitivity: 'base' });
          });

          // 새로운 배열 생성으로 반응성 보장
          storeList.value = [...sortedList.map(store => ({
            ...store,
            isOpen: false,
            isReady: false
          }))];

          total.value = data.page.total;

          // 나이키 캐시 초기화
          nikeFullSortedList.value = [];
        }

        console.log('총 매장 수 업데이트:', total.value);
      } else {
        console.log('매장 데이터가 비어있거나 잘못된 형식:', data);
        storeList.value = [];
        total.value = 0;
      }
    }, {
      immediate: true, // 즉시 실행
      deep: true // 깊은 감시
    });

    // 브랜드에 실제 매장이 있는 지역만 노출
    const regionData = useQuery({
      queryKey: ['branch-store-region', branchUuid, brandUuid],
      queryFn: () => getAxios('/hvm/v1/branch/store/region/list', {
        branchUuid: branchUuid.value,
        brandUuid: brandUuid.value,
      }),
      enabled: computed(() => Boolean(branchUuid.value)),
    });

    watch(() => regionData.data.value, (data) => {
      const regions = data?.page?.list || [];
      regionList.value = [{ name: '전체', value: '' }].concat(
        regions
          .map(item => item.regionName)
          .filter(Boolean)
          .map(regionName => ({ name: regionName, value: regionName }))
      );
    }, { immediate: true });

    // 시도 변경 시
    watch(() => selectedSido.value, (newData) => {
      console.log('시도 변경:', newData);
      storeReqData.sido = newData;
    });

    // 페이지 변경 시 storeReqData 동기화
    watch(() => currentPage.value, (newPage) => {
      console.log('페이지 변경:', newPage);

      // 나이키 검색이고 캐시된 데이터가 있으면 캐시에서 슬라이싱
      if (storeReqData.name && storeReqData.name.includes('나이키') && nikeFullSortedList.value.length > 0) {
        console.log('나이키 캐시 데이터에서 페이징 처리');

        const pageSize = storeReqData.size || 10;
        const startIndex = (newPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedList = nikeFullSortedList.value.slice(startIndex, endIndex);

        console.log(`페이지 ${newPage}: 시작=${startIndex}, 끝=${endIndex}`);
        console.log('현재 페이지 데이터:', paginatedList.map((s, idx) => `${startIndex + idx + 1}. ${s.storeName}`));

        storeList.value = [...paginatedList.map(store => ({
          ...store,
          isOpen: false,
          isReady: false
        }))];
      }

      storeReqData.page = newPage;
    });

    // 위치 정보 초기화 및 첫 번째 API 호출
    const initializeData = async () => {
      console.log('데이터 초기화 시작');

      try {
        // 위치 정보 가져오기
        if (navigator.geolocation) {
          await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                const latitude = position.coords.latitude;
                const longitude = position.coords.longitude;

                console.log('위치 정보 획득:', latitude, longitude);
                storeReqData.latitude = latitude;
                storeReqData.longitude = longitude;
                resolve();
              },
              (err) => {
                console.log('위치 정보 획득 실패:', err.message);
                storeReqData.latitude = 0;
                storeReqData.longitude = 0;
                resolve();
              }
            );
          });
        } else {
          console.log('Geolocation 미지원');
          storeReqData.latitude = 0;
          storeReqData.longitude = 0;
        }

        // sessionStorage에서 브랜드명 확인
        let paramBrandName = route.query.brandName;
        if (typeof paramBrandName === "string" && paramBrandName !== "") {
          console.log('URL 파라미터로 브랜드명 설정:', paramBrandName);
          storeReqData.name = paramBrandName;
        } else {
          // 2. sessionStorage에서 브랜드명 확인
          const brandName = sessionStorage.getItem('brandName');
          if (brandName) {
            console.log('브랜드명 설정:', brandName);
            storeReqData.name = brandName;
            sessionStorage.removeItem('brandName'); // 사용 후 제거
          }
        }

        // 매장 데이터 호출
        console.log('매장 데이터 호출 시작');
        await storeData.refetch();
        console.log('매장 데이터 호출 완료');

      } catch (error) {
        console.error('데이터 초기화 중 오류:', error);
      }
    };

    // 첫 방문 시 지점 설정이 비동기로 저장되므로 저장될 때까지 기다린 뒤 지역 목록을 조회
    let branchUuidTimer = null;
    const waitForBranchUuid = () => {
      let tryCount = 0;
      branchUuidTimer = setInterval(() => {
        const uuid = getConfig()?.branch?.branchUuid;
        tryCount += 1;
        if (uuid) {
          branchUuid.value = uuid;
        }
        if (uuid || tryCount >= 40) {
          clearInterval(branchUuidTimer);
          branchUuidTimer = null;
        }
      }, 250);
    };

    onMounted(() => {
      console.log('HFO_MS_001 컴포넌트 마운트됨');

      // 화면 크기 체크
      isPc.value = window.innerWidth >= 1024;

      if (!branchUuid.value) {
        waitForBranchUuid();
      }

      // 데이터 초기화
      initializeData();
    });

    onBeforeUnmount(() => {
      if (branchUuidTimer) {
        clearInterval(branchUuidTimer);
      }
    });

    return {
      regionList,
      storeList,
      selectedSido,
      currentPage,
      pageSize,
      total,
      storeData,
      storeReqData,
      isPc,
      isLocationReady,
      isManualLoading, // isManualLoading 추가
    };
  },
  methods: {
    async selectRegion(region) {
      const sidoName = region?.value ? region.name : '';
      this.selectedSido = sidoName;
      this.storeReqData.sido = sidoName;
      this.currentPage = 1;
      this.storeReqData.page = 1;
      await this.search();
    },

    openMap(index, latitude, longitude, event) {
      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      console.log('맵 열기 요청:', latitude, longitude);

      if (latitude == 0 || longitude == 0) {
        this.alertRequiredShow = true;
        return;
      }

      const isCurrentlyOpen = this.storeList[index].isOpen;

      sessionStorage.setItem("latitude", latitude);
      sessionStorage.setItem("longitude", longitude);

      if (isCurrentlyOpen) {
        this.storeList[index].isOpen = false;
        this.storeList[index].isReady = false;
        return;
      }

      this.storeList.forEach((store) => {
        store.isOpen = false;
        store.isReady = false;
      });

      if (this._mapOpenTimeout) {
        clearTimeout(this._mapOpenTimeout);
      }

      this._mapOpenTimeout = setTimeout(() => {
        this.storeList[index].isOpen = true;
        setTimeout(() => {
          this.storeList[index].isReady = true;
        }, 100);
      }, 50);
    },

    async onPageChange(newPage) {
      console.log('페이지 변경:', newPage);
      this.currentPage = newPage;
      this.storeReqData.page = newPage;

      try {
        // 즉시 로딩 상태 활성화
        this.isManualLoading = true;

        await this.storeData.refetch();
        console.log('페이지 변경 후 데이터 로드 완료');
      } catch (error) {
        console.error('페이지 변경 중 오류:', error);
      } finally {
        // 로딩 완료 후 수동 로딩 상태 해제
        this.isManualLoading = false;
      }
    },

    async search() {
      console.log('검색 실행:', this.storeReqData);

      // 검색 실행 시 페이지를 1로 초기화
      this.currentPage = 1;
      this.storeReqData.page = 1;

      try {
        // 즉시 로딩 상태 활성화 (모바일 딜레이 해결)
        this.isManualLoading = true;

        await this.storeData.refetch();
        console.log('검색 완료');

        // 검색 후 포커스 해제 (선택사항)
        if (document.activeElement && document.activeElement.blur) {
          document.activeElement.blur();
        }

      } catch (error) {
        console.error('검색 중 오류:', error);
      } finally {
        // 로딩 완료 후 수동 로딩 상태 해제
        this.isManualLoading = false;
      }
    }
  }
}
</script>

<style scoped>
/* 로딩 레이어 스타일 */
.loading_overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 8px;
}

.loading_content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading_spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #ee7527;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading_text {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0;
}

.region-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.region-chip-list button {
  min-width: 58px;
  height: 36px;
  padding: 0 16px;
  border: 1px solid #e2e2e2;
  border-radius: 20px;
  background: #f7f7f7;
  color: #888;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}

.region-chip-list button.active {
  border-color: #34405d;
  background: #34405d;
  color: #fff;
  font-weight: 700;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

/* search_result_area에 relative position 추가 */
.search_result_area {
  position: relative;
}

/* 새로운 버튼 스타일 추가 (선택 사항, Tailwind 또는 커스텀 CSS 사용 가능) */
.btn_map_view {
  padding: 8px 16px;
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn_map_view:hover {
  background-color: #357abd;
}

.search_result_num {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

:deep(.list_tb thead th) {
  background-color: #34405d;
  color: #fff;
}

.btn_map_view {
  margin-left: auto;
}

/* HFO_MS_001 전용: 큰 페이지 번호(예: 2000+)도 겹치지 않도록 유동 폭 처리 */
:deep(.paging_area) {
  flex-wrap: wrap;
  align-items: center;
}

:deep(.paging_area .paging_num) {
  min-width: 24px;
  display: inline-flex;
  justify-content: center;
}

:deep(.paging_area .paging_num button) {
  width: auto;
  min-width: 24px;
  padding: 0 4px;
  white-space: nowrap;
}

/* 지도 영역 여백 제거 - 모바일 기본(300px) */
.map_area {
  margin-bottom: 0;
  padding-bottom: 0;
  min-height: 300px !important;
  /* 모바일 기본 높이 300px */
  overflow: hidden;
}

/* PC 버전(1024px 이상)에서는 480px로 설정 */
@media screen and (min-width: 1024px) {
  .map_area {
    min-height: 480px !important;
    /* PC 버전 높이 480px */
  }
}

/* PC 버전 지도 스타일 */
:deep(.naver-map) {
  width: 100% !important;
  height: 500px !important;
  /* 적절한 높이 설정 */
  margin-bottom: 0 !important;
}

/* 추가 여백 제거 */
:deep(.map-container) {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}
</style>